'use client';

/**
 * AnchorSection — wrapper viewport-sized per section premium.
 *
 * Garantisce `min-height = window.innerHeight` (runtime) così quando user
 * scrolla all'anchor (top section = top viewport), le waves a fondo container
 * coincidono con viewport bottom.
 *
 * Strategia:
 *  - CSS fallback `min-h-[100svh]` (small viewport height, evita URL-bar jump mobile)
 *  - JS override su mount + resize con `window.innerHeight` precision
 *  - flex column: contenuto naturale, waves `mt-auto` spinte a bottom
 *
 * Se contenuto > vh, container espande e waves seguono flow naturale.
 */

import React, { useEffect, useState } from 'react';
import MaritimeFooterWaves from './MaritimeFooterWaves';
import AmbientBackground from './AmbientBackground';

interface Props {
  id?: string;
  children: React.ReactNode;
  waves?: boolean;
  variant?: 'light' | 'dark';
  transparent?: boolean;
  topCue?: React.ReactNode;
  bottomCue?: React.ReactNode;
}

const AnchorSection: React.FC<Props> = ({ id, children, waves = true, variant = 'light', transparent = false, topCue, bottomCue }) => {
  const [vh, setVh] = useState<number | null>(null);

  useEffect(() => {
    const update = () => setVh(window.innerHeight);
    update();
    window.addEventListener('resize', update);
    window.addEventListener('orientationchange', update);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('orientationchange', update);
    };
  }, []);

  return (
    <section
      id={id}
      className="relative flex flex-col h-[100svh] snap-start overflow-hidden"
      style={vh ? { height: `${vh}px` } : undefined}
    >
      {!transparent && <AmbientBackground variant={variant} position="absolute" />}
      {/* Overlay leggibilità: solo se transparent (bg dietro è hero scuro) */}
      {transparent && <div className="absolute inset-0 z-0 bg-slate-950/40" aria-hidden />}
      <div className={`relative z-10 flex-1 flex flex-col overflow-y-auto ${topCue ? 'pt-32' : ''} ${bottomCue ? 'pb-32' : ''}`}>{children}</div>
      {!transparent && waves && (
        <div className="relative z-10 shrink-0">
          <MaritimeFooterWaves variant={variant} />
        </div>
      )}
      {/* Scroll cues agli estremi della section */}
      {topCue && (
        <div className="absolute top-20 left-1/2 -translate-x-1/2 z-20">
          {topCue}
        </div>
      )}
      {bottomCue && (
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
          {bottomCue}
        </div>
      )}
    </section>
  );
};

export default AnchorSection;
